import React, { useState, useMemo, useEffect, useRef } from 'react';
import Layout from '../components/Layout';
import { mockContentAssets, mockTrends } from '../data/index';
import type { ContentAsset, ApprovalStatus } from '../lib/index';
import { getStoredBundles, type GeneratedBundle } from '../services/openai';
import {
  FileText,
  Image,
  Video,
  Download,
  Eye,
  Calendar,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Layers,
  TrendingUp,
  X,
  Copy,
  Check,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';

type FilterType = 'all' | 'article' | 'infographic' | 'video' | 'generated' | 'approved' | 'scheduled';

/* ─────────────────────────────────────────────────────── helpers ── */

const getStatusBadge = (status: ApprovalStatus) => {
  const cfg: Record<ApprovalStatus, { label: string; cls: string; icon: React.ReactNode }> = {
    draft:           { label: 'Draft',          cls: 'bg-gray-100 text-gray-700',   icon: <AlertCircle className="w-3 h-3" /> },
    'pending-review':{ label: 'Pending Review', cls: 'bg-yellow-100 text-yellow-700',icon: <Clock className="w-3 h-3" /> },
    approved:        { label: 'Approved',        cls: 'bg-green-100 text-green-700',  icon: <CheckCircle className="w-3 h-3" /> },
    rejected:        { label: 'Rejected',        cls: 'bg-red-100 text-red-700',     icon: <XCircle className="w-3 h-3" /> },
    published:       { label: 'Published',       cls: 'bg-blue-100 text-blue-700',   icon: <CheckCircle className="w-3 h-3" /> },
    scheduled:       { label: 'Scheduled',       cls: 'bg-purple-100 text-purple-700',icon: <Calendar className="w-3 h-3" /> },
    archived:        { label: 'Archived',        cls: 'bg-gray-100 text-gray-500',   icon: <AlertCircle className="w-3 h-3" /> },
  };
  const c = cfg[status];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${c.cls}`}>
      {c.icon}{c.label}
    </span>
  );
};

const fmtDate = (d: Date | string) =>
  new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(new Date(d));

const weekLabel = (d: Date) => {
  const wn = Math.ceil((d.getTime() - new Date(d.getFullYear(), 0, 1).getTime()) / (7 * 24 * 60 * 60 * 1000));
  return `Week ${wn}`;
};

/** Download any text content as a .txt / .md file */
function downloadText(filename: string, content: string) {
  const blob = new Blob([content], { type: 'text/plain' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/** Download a base64 or object-URL image as a .png */
function downloadImage(filename: string, src: string) {
  if (src.startsWith('data:') || src.startsWith('blob:')) {
    const a = document.createElement('a');
    a.href = src;
    a.download = filename;
    a.click();
  } else {
    // External URL — open in new tab so user can save manually
    window.open(src, '_blank');
  }
}

/* ─────────────────────── Full-content modal ─────────────────────── */

interface FullViewModalProps {
  bundle: GeneratedBundle;
  tab: 'blog' | 'infographic' | 'comic' | 'reel';
  onClose: () => void;
}

const FullViewModal: React.FC<FullViewModalProps> = ({ bundle, tab: initialTab, onClose }) => {
  const [tab, setTab] = useState(initialTab);
  const [copied, setCopied] = useState(false);
  const backdropRef = useRef<HTMLDivElement>(null);

  // infographic / comic — read from URL-only maps (v2, avoids base64 quota)
  const infographicImg = (() => {
    try {
      const map: Record<string, string | null> = JSON.parse(localStorage.getItem('uxcf_infographic_urls') || '{}');
      return map[bundle.id] || null;
    } catch { return null; }
  })();
  const comicImg = (() => {
    try {
      const map: Record<string, string | null> = JSON.parse(localStorage.getItem('uxcf_comic_urls') || '{}');
      return map[bundle.id] || null;
    } catch { return null; }
  })();

  const tabs = [
    { id: 'blog' as const,        label: '📝 Blog Post',    available: !!bundle.blogPost },
    { id: 'infographic' as const, label: '📊 Infographic',  available: !!infographicImg },
    { id: 'comic' as const,       label: '🎨 Comic Strip',  available: !!comicImg },
    { id: 'reel' as const,        label: '🎬 Reel Script',  available: !!bundle.reelScript },
  ].filter(t => t.available);

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleDownload = () => {
    if (tab === 'blog' && bundle.blogPost) {
      const md = `# ${bundle.blogPost.title}\n\n${bundle.blogPost.body || bundle.blogPost.excerpt || ''}`;
      downloadText(`${bundle.trendName.replace(/\s+/g,'_')}_blog.md`, md);
    }
    if (tab === 'reel' && bundle.reelScript) {
      const rs = bundle.reelScript as any;
      const text = [
        `REEL SCRIPT: ${rs.title || bundle.trendName}`,
        `Hook: ${rs.hook || ''}`,
        '',
        ...(rs.scenes || []).map((s: any, i: number) => `Scene ${i+1}: ${s.text || s}`),
        '',
        `CTA: ${rs.cta || ''}`,
      ].join('\n');
      downloadText(`${bundle.trendName.replace(/\s+/g,'_')}_reel_script.txt`, text);
    }
    if (tab === 'infographic' && infographicImg) {
      downloadImage(`${bundle.trendName.replace(/\s+/g,'_')}_infographic.png`, infographicImg);
    }
    if (tab === 'comic' && comicImg) {
      downloadImage(`${bundle.trendName.replace(/\s+/g,'_')}_comic.png`, comicImg);
    }
  };

  // Close on Escape key
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const blogBody   = (bundle.blogPost as any)?.body   || bundle.blogPost?.excerpt || '';
  const reelScript = bundle.reelScript as any;

  return (
    <div
      ref={backdropRef}
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-2 sm:p-4"
      onClick={(e) => { if (e.target === backdropRef.current) onClose(); }}
    >
      <div className="bg-slate-900 border border-slate-700 rounded-t-2xl sm:rounded-2xl w-full max-w-3xl max-h-[92dvh] flex flex-col shadow-2xl">
        {/* Modal header */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-slate-700 shrink-0">
          <div className="min-w-0">
            <p className="text-xs text-violet-400 uppercase tracking-wider mb-0.5">AI Generated Bundle</p>
            <h2 className="text-base sm:text-lg font-bold text-white truncate max-w-[200px] sm:max-w-md">{bundle.trendName}</h2>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleDownload}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-violet-600 hover:bg-violet-500 text-white text-xs font-medium rounded-lg transition-colors"
            >
              <Download className="w-3.5 h-3.5" />
              Download
            </button>
            <button onClick={onClose} className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700 transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        {tabs.length > 1 && (
          <div className="flex items-center gap-1 px-6 pt-3 shrink-0">
            {tabs.map(t => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  tab === t.id
                    ? 'bg-violet-600 text-white'
                    : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        )}

        {/* Modal body — scrollable */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">

          {/* ── Blog Post ── */}
          {tab === 'blog' && bundle.blogPost && (
            <div className="space-y-4">
              <div className="flex items-start justify-between gap-3">
                <h3 className="text-white text-xl font-bold leading-snug">{bundle.blogPost.title}</h3>
                <button
                  onClick={() => handleCopy(blogBody)}
                  className="shrink-0 flex items-center gap-1.5 px-2.5 py-1 bg-slate-700 hover:bg-slate-600 text-slate-300 text-xs rounded-lg transition-colors"
                >
                  {copied ? <Check className="w-3 h-3 text-green-400" /> : <Copy className="w-3 h-3" />}
                  {copied ? 'Copied!' : 'Copy'}
                </button>
              </div>

              {/* Meta row */}
              <div className="flex flex-wrap items-center gap-3 text-xs text-slate-400">
                {bundle.blogPost.wordCount && <span>📄 {bundle.blogPost.wordCount.toLocaleString()} words</span>}
                {(bundle.blogPost as any).readTime && <span>⏱ {(bundle.blogPost as any).readTime} min read</span>}
                {bundle.blogPost.seoScore && <span>🎯 SEO {bundle.blogPost.seoScore}/100</span>}
                {bundle.blogPost.tags && (bundle.blogPost.tags as string[]).map((t: string, i: number) => (
                  <span key={i} className="px-2 py-0.5 bg-slate-700 rounded">#{t}</span>
                ))}
              </div>

              {/* Excerpt */}
              {bundle.blogPost.excerpt && (
                <div className="bg-violet-900/20 border border-violet-700/30 rounded-xl p-4">
                  <p className="text-violet-200 text-sm italic leading-relaxed">{bundle.blogPost.excerpt}</p>
                </div>
              )}

              {/* Full body */}
              <div className="bg-slate-800 rounded-xl p-5">
                {blogBody ? (
                  <div className="text-slate-200 text-sm leading-relaxed whitespace-pre-wrap font-mono">
                    {blogBody}
                  </div>
                ) : (
                  <p className="text-slate-500 text-sm italic">Full body not stored — regenerate in Content Pipeline.</p>
                )}
              </div>

              {/* Infographic brief if exists */}
              {bundle.infographic && (
                <details className="group bg-slate-800 rounded-xl">
                  <summary className="flex items-center justify-between px-4 py-3 cursor-pointer list-none text-slate-300 text-sm font-medium">
                    <span>📊 Infographic Brief</span>
                    <ChevronDown className="w-4 h-4 group-open:hidden" />
                    <ChevronUp   className="w-4 h-4 hidden group-open:block" />
                  </summary>
                  <div className="px-4 pb-4 space-y-2">
                    <p className="text-slate-200 font-medium">{bundle.infographic.title}</p>
                    {bundle.infographic.statHighlights?.map((s, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <span className="text-violet-300 font-bold text-lg">{s.value}</span>
                        <span className="text-slate-400 text-sm">{s.label}</span>
                      </div>
                    ))}
                    {(bundle.infographic as any).sections?.map((sec: any, i: number) => (
                      <div key={i} className="bg-slate-700/50 rounded-lg p-3">
                        <p className="text-slate-300 font-semibold text-xs uppercase tracking-wider">{sec.heading}</p>
                        <p className="text-slate-400 text-xs mt-1">{sec.body}</p>
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </div>
          )}

          {/* ── Infographic image ── */}
          {tab === 'infographic' && infographicImg && (
            <div className="flex flex-col items-center gap-4">
              <img
                src={infographicImg}
                alt="Generated Infographic"
                className="w-full max-w-lg rounded-2xl shadow-xl border border-slate-700"
              />
              <p className="text-slate-400 text-xs">Click "Download" above to save as PNG.</p>
            </div>
          )}

          {/* ── Comic Strip image ── */}
          {tab === 'comic' && comicImg && (
            <div className="flex flex-col items-center gap-4">
              <img
                src={comicImg}
                alt="Generated Comic Strip"
                className="w-full rounded-2xl shadow-xl border border-slate-700"
              />
              <p className="text-slate-400 text-xs">Click "Download" above to save as PNG.</p>
            </div>
          )}

          {/* ── Reel Script ── */}
          {tab === 'reel' && reelScript && (
            <div className="space-y-4">
              <div className="flex items-start justify-between gap-3">
                <h3 className="text-white text-xl font-bold leading-snug">{reelScript.title || bundle.trendName}</h3>
                <button
                  onClick={() => {
                    const text = [
                      `Hook: ${reelScript.hook || ''}`,
                      '',
                      ...(reelScript.scenes || []).map((s: any, i: number) => `Scene ${i+1}: ${s.text || s}`),
                      '',
                      `CTA: ${reelScript.cta || ''}`,
                    ].join('\n');
                    handleCopy(text);
                  }}
                  className="shrink-0 flex items-center gap-1.5 px-2.5 py-1 bg-slate-700 hover:bg-slate-600 text-slate-300 text-xs rounded-lg transition-colors"
                >
                  {copied ? <Check className="w-3 h-3 text-green-400" /> : <Copy className="w-3 h-3" />}
                  {copied ? 'Copied!' : 'Copy'}
                </button>
              </div>

              <div className="flex flex-wrap items-center gap-3 text-xs text-slate-400">
                {reelScript.duration && <span>⏱ {reelScript.duration}</span>}
                {reelScript.scenes?.length && <span>🎞 {reelScript.scenes.length} scenes</span>}
                {reelScript.platform && <span>📱 {reelScript.platform}</span>}
              </div>

              {/* Hook */}
              {reelScript.hook && (
                <div className="bg-amber-900/20 border border-amber-700/30 rounded-xl p-4">
                  <p className="text-xs text-amber-400 font-semibold uppercase tracking-wider mb-1">🎣 Hook</p>
                  <p className="text-amber-100 text-sm leading-relaxed font-medium">{reelScript.hook}</p>
                </div>
              )}

              {/* Scenes */}
              {reelScript.scenes && reelScript.scenes.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold">Scenes</p>
                  {reelScript.scenes.map((scene: any, i: number) => (
                    <div key={i} className="bg-slate-800 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="w-6 h-6 rounded-full bg-violet-600 flex items-center justify-center text-white text-xs font-bold shrink-0">{i + 1}</span>
                        {scene.timestamp && <span className="text-slate-500 text-xs">{scene.timestamp}</span>}
                        {scene.visual && <span className="text-slate-500 text-xs italic">{scene.visual}</span>}
                      </div>
                      <p className="text-slate-200 text-sm leading-relaxed">{scene.text || scene.narration || scene.script || String(scene)}</p>
                      {scene.bRoll && <p className="text-slate-500 text-xs mt-1">B-roll: {scene.bRoll}</p>}
                    </div>
                  ))}
                </div>
              )}

              {/* CTA */}
              {reelScript.cta && (
                <div className="bg-green-900/20 border border-green-700/30 rounded-xl p-4">
                  <p className="text-xs text-green-400 font-semibold uppercase tracking-wider mb-1">✅ Call to Action</p>
                  <p className="text-green-100 text-sm">{reelScript.cta}</p>
                </div>
              )}

              {/* Hashtags / Caption */}
              {reelScript.caption && (
                <div className="bg-slate-800 rounded-xl p-4">
                  <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold mb-1">Caption</p>
                  <p className="text-slate-300 text-sm whitespace-pre-wrap">{reelScript.caption}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/* ───────────────────────────── Main page ─────────────────────────── */

const AssetLibrary: React.FC = () => {
  const [activeFilter, setActiveFilter] = useState<FilterType>('all');
  const [quickFilter, setQuickFilter] = useState<'all' | 'approved' | 'scheduled'>('all');
  const [selectedAsset,  setSelectedAsset]  = useState<ContentAsset | null>(null);
  const [selectedBundle, setSelectedBundle] = useState<GeneratedBundle | null>(null);
  const [generatedBundles, setGeneratedBundles] = useState<GeneratedBundle[]>([]);

  // Full-view modal state
  const [modalBundle, setModalBundle] = useState<GeneratedBundle | null>(null);
  const [modalTab, setModalTab] = useState<'blog' | 'infographic' | 'comic' | 'reel'>('blog');

  // copy-to-clipboard for panel
  const [panelCopied, setPanelCopied] = useState(false);

  useEffect(() => {
    const bundles = getStoredBundles();
    setGeneratedBundles(bundles);
    // Auto-select the first generated bundle if available
    if (bundles.length > 0) {
      setSelectedBundle(bundles[0]);
    }
  }, []);

  const filteredAssets = useMemo(() => {
    let base = mockContentAssets;
    if (activeFilter === 'article')     base = base.filter(a => a.contentType === 'article');
    if (activeFilter === 'infographic') base = base.filter(a => a.contentType === 'infographic');
    if (activeFilter === 'video')       base = base.filter(a => a.contentType === 'video');
    if (quickFilter === 'approved')  base = base.filter(a => a.status === 'approved');
    if (quickFilter === 'scheduled') base = base.filter(a => a.status === 'scheduled');
    return base;
  }, [activeFilter, quickFilter]);

  const assetCounts = useMemo(() => ({
    blogPosts:    mockContentAssets.filter(a => a.contentType === 'article').length + generatedBundles.length,
    infographics: mockContentAssets.filter(a => a.contentType === 'infographic').length + generatedBundles.length,
    reels:        mockContentAssets.filter(a => a.contentType === 'video').length + generatedBundles.length,
  }), [generatedBundles]);

  const getAssetIcon = (ct: string) => {
    if (ct === 'article')     return <FileText className="w-5 h-5" />;
    if (ct === 'infographic') return <Image    className="w-5 h-5" />;
    if (ct === 'video')       return <Video    className="w-5 h-5" />;
    return <Layers className="w-5 h-5" />;
  };

  const getTrendName = (id: string) => mockTrends.find(t => t.id === id)?.name || 'Unknown Trend';

  // Get stored images for a bundle — reads from URL-only maps (v2)
  const getBundleImages = (bundle: GeneratedBundle) => {
    try {
      const iMap: Record<string, string | null> = JSON.parse(localStorage.getItem('uxcf_infographic_urls') || '{}');
      const cMap: Record<string, string | null> = JSON.parse(localStorage.getItem('uxcf_comic_urls') || '{}');
      return {
        infographic: iMap[bundle.id] || null,
        comic: cMap[bundle.id] || null,
      };
    } catch {
      return { infographic: null, comic: null };
    }
  };

  const openModal = (bundle: GeneratedBundle, tab: 'blog' | 'infographic' | 'comic' | 'reel') => {
    setModalBundle(bundle);
    setModalTab(tab);
  };

  const handleBundleDownloadAll = (bundle: GeneratedBundle) => {
    const imgs = getBundleImages(bundle);
    // Blog post
    if (bundle.blogPost) {
      const md = `# ${bundle.blogPost.title}\n\n${(bundle.blogPost as any).body || bundle.blogPost.excerpt || ''}`;
      downloadText(`${bundle.trendName.replace(/\s+/g,'_')}_blog.md`, md);
    }
    // Reel script
    if (bundle.reelScript) {
      const rs = bundle.reelScript as any;
      const text = [
        `REEL SCRIPT: ${rs.title || bundle.trendName}`,
        `Hook: ${rs.hook || ''}`,
        '',
        ...(rs.scenes || []).map((s: any, i: number) => `Scene ${i+1}: ${s.text || s}`),
        '',
        `CTA: ${rs.cta || ''}`,
      ].join('\n');
      downloadText(`${bundle.trendName.replace(/\s+/g,'_')}_reel_script.txt`, text);
    }
    // Images
    if (imgs.infographic) downloadImage(`${bundle.trendName.replace(/\s+/g,'_')}_infographic.png`, imgs.infographic);
    if (imgs.comic)       downloadImage(`${bundle.trendName.replace(/\s+/g,'_')}_comic.png`,       imgs.comic);
  };

  const handlePanelCopy = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setPanelCopied(true);
      setTimeout(() => setPanelCopied(false), 2000);
    });
  };

  return (
    <Layout>
      {/* Full-view modal */}
      {modalBundle && (
        <FullViewModal
          bundle={modalBundle}
          tab={modalTab}
          onClose={() => setModalBundle(null)}
        />
      )}

      <div className="p-3 sm:p-6 space-y-4 sm:space-y-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-slate-100">Asset Library</h1>
            <p className="text-slate-500 mt-0.5 text-sm">Manage & organise your publish-ready content assets</p>
          </div>
          <div className="flex items-center gap-3 text-sm flex-wrap">
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-violet-500/10 border border-violet-500/20 rounded-xl">
              <FileText className="w-3.5 h-3.5 text-violet-400" />
              <span className="font-bold text-violet-400">{assetCounts.blogPosts}</span>
              <span className="text-slate-500 text-xs">Blogs</span>
            </div>
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-cyan-500/10 border border-cyan-500/20 rounded-xl">
              <Image className="w-3.5 h-3.5 text-cyan-400" />
              <span className="font-bold text-cyan-400">{assetCounts.infographics}</span>
              <span className="text-slate-500 text-xs">Infographics</span>
            </div>
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-fuchsia-500/10 border border-fuchsia-500/20 rounded-xl">
              <Video className="w-3.5 h-3.5 text-fuchsia-400" />
              <span className="font-bold text-fuchsia-400">{assetCounts.reels}</span>
              <span className="text-slate-500 text-xs">Reels</span>
            </div>
          </div>
        </div>

        {/* Quick Filters */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[11px] text-slate-600 font-semibold uppercase tracking-wider mr-1">Quick Filters:</span>
          {([['all','All'],['approved','✓ Approved'],['scheduled','🗓 Scheduled']] as const).map(([v, label]) => (
            <button
              key={v}
              onClick={() => setQuickFilter(v as 'all' | 'approved' | 'scheduled')}
              className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${
                quickFilter === v
                  ? 'bg-violet-500/15 text-violet-400 border border-violet-500/20'
                  : 'text-slate-500 hover:text-slate-300 bg-slate-800/40 border border-transparent'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Filter Tabs */}
        <div className="flex items-center gap-0 border-b border-slate-800/60 overflow-x-auto scrollbar-hide">
          {(['all', 'generated', 'article', 'infographic', 'video'] as FilterType[]).map(f => (
            <button
              key={f}
              onClick={() => { setActiveFilter(f); if (f === 'generated') { setSelectedAsset(null); } else { setSelectedBundle(null); } }}
              className={`px-3 sm:px-4 py-2 font-medium text-xs sm:text-sm border-b-2 transition-colors capitalize whitespace-nowrap ${
                activeFilter === f
                  ? 'border-violet-500 text-violet-400'
                  : 'border-transparent text-slate-500 hover:text-slate-300'
              }`}
            >
              {f === 'generated' ? `🤖 AI Bundles${generatedBundles.length ? ` (${generatedBundles.length})` : ''}` :
               f === 'all' ? 'All Assets' :
               f === 'article' ? 'Blog Posts' :
               f === 'video' ? 'Reels' :
               'Infographics'}
            </button>
          ))}
        </div>

        {/* Main Content Grid — 30% sidebar + 70% content */}
        <div className="grid grid-cols-1 lg:grid-cols-[30%_1fr] gap-6">

          {/* Left: Asset / Bundle list */}
          <div className="lg:col-span-2 space-y-4">

            {/* ── AI Bundles tab ── */}
            {activeFilter === 'generated' && (
              generatedBundles.length === 0 ? (
                <div className="flex flex-col items-center py-16 gap-3 text-slate-400">
                  <Layers className="w-12 h-12 opacity-30" />
                  <p className="font-medium">No AI bundles yet</p>
                  <p className="text-sm text-center max-w-xs">Run the 3-Agent Pipeline in Content Pipeline to generate your first bundle.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {generatedBundles.map(bundle => {
                    const imgs = getBundleImages(bundle);
                    return (
                      <div
                        key={bundle.id}
                        onClick={() => { setSelectedBundle(bundle); setSelectedAsset(null); }}
                        className={`bg-slate-900 border rounded-2xl p-4 cursor-pointer transition-all ${
                          selectedBundle?.id === bundle.id ? 'border-violet-500 shadow-lg shadow-violet-500/10' : 'border-slate-700 hover:border-slate-500'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2 mb-3">
                          <div className="min-w-0">
                            <p className="text-slate-100 font-semibold truncate">{bundle.trendName}</p>
                            <p className="text-slate-500 text-xs mt-0.5">{fmtDate(bundle.generatedAt)} · {bundle.blogPost?.wordCount?.toLocaleString()} words</p>
                          </div>
                          <span className={`px-2 py-0.5 text-xs rounded-full shrink-0 ${
                            bundle.approvalStatus === 'approved' ? 'bg-green-500/20 text-green-400' :
                            bundle.approvalStatus === 'rejected' ? 'bg-red-500/20 text-red-400' :
                            'bg-amber-500/20 text-amber-400'
                          }`}>{bundle.approvalStatus}</span>
                        </div>

                        {/* Asset badges */}
                        <div className="flex flex-wrap gap-2 mb-3">
                          <span className="px-2 py-0.5 bg-blue-500/10 text-blue-300 text-xs rounded-full">📝 Blog</span>
                          {imgs.infographic && <span className="px-2 py-0.5 bg-purple-500/10 text-purple-300 text-xs rounded-full">📊 Infographic</span>}
                          {imgs.comic       && <span className="px-2 py-0.5 bg-amber-500/10  text-amber-300  text-xs rounded-full">🎨 Comic</span>}
                          {bundle.reelScript && <span className="px-2 py-0.5 bg-pink-500/10 text-pink-300 text-xs rounded-full">🎬 Reel</span>}
                        </div>

                        {/* Quick-action buttons */}
                        <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                          <button
                            onClick={() => openModal(bundle, 'blog')}
                            className="flex items-center gap-1 px-3 py-1.5 bg-slate-700 hover:bg-violet-700 text-slate-200 text-xs font-medium rounded-lg transition-colors"
                          >
                            <Eye className="w-3.5 h-3.5" /> View Full
                          </button>
                          <button
                            onClick={() => handleBundleDownloadAll(bundle)}
                            className="flex items-center gap-1 px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-200 text-xs font-medium rounded-lg transition-colors"
                          >
                            <Download className="w-3.5 h-3.5" /> Download All
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )
            )}

            {/* ── Mock asset cards (other tabs) ── */}
            {activeFilter !== 'generated' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {filteredAssets.map(asset => (
                  <div
                    key={asset.id}
                    onClick={() => { setSelectedAsset(asset); setSelectedBundle(null); }}
                    className={`bg-white rounded-xl border-2 p-4 cursor-pointer transition-all hover:shadow-md ${
                      selectedAsset?.id === asset.id ? 'border-indigo-600 shadow-md' : 'border-gray-200'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className={`p-2 rounded-lg ${
                          asset.contentType === 'article'     ? 'bg-blue-100 text-blue-600' :
                          asset.contentType === 'infographic' ? 'bg-purple-100 text-purple-600' :
                          'bg-pink-100 text-pink-600'
                        }`}>{getAssetIcon(asset.contentType)}</div>
                        <span className="text-xs font-medium text-gray-500">{weekLabel(asset.createdAt)}</span>
                      </div>
                      {getStatusBadge(asset.status)}
                    </div>

                    <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2">{asset.title}</h3>

                    <div className="flex items-center gap-1 text-xs text-gray-500 mb-3">
                      <TrendingUp className="w-3 h-3" />
                      <span>{getTrendName(asset.trendTopicId)}</span>
                    </div>

                    <div className="flex items-center justify-between text-xs text-gray-400 mb-3">
                      <span>{fmtDate(asset.createdAt)}</span>
                      <span>
                        {asset.wordCount && `${asset.wordCount} words`}
                        {asset.dimensions && `${asset.dimensions.width}×${asset.dimensions.height}`}
                        {asset.duration && `${asset.duration}s`}
                      </span>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        onClick={e => { e.stopPropagation(); setSelectedAsset(asset); setSelectedBundle(null); }}
                        className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-xs font-medium hover:bg-indigo-700 transition-colors"
                      >
                        <Eye className="w-3 h-3" /> View
                      </button>
                      <button
                        onClick={e => {
                          e.stopPropagation();
                          const content = `Title: ${asset.title}\n\nDescription: ${asset.description}\n\nTrend: ${getTrendName(asset.trendTopicId)}\nTags: ${asset.tags.join(', ')}`;
                          downloadText(`${asset.title.replace(/\s+/g,'_')}.txt`, content);
                        }}
                        className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-xs font-medium hover:bg-gray-200 transition-colors"
                      >
                        <Download className="w-3 h-3" /> Download
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* ── If 'all' tab, also show AI bundles section below ── */}
            {activeFilter === 'all' && generatedBundles.length > 0 && (
              <div className="mt-6">
                <h3 className="text-sm font-semibold text-violet-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-violet-400 animate-pulse inline-block" />
                  AI Generated Bundles ({generatedBundles.length})
                </h3>
                <div className="space-y-3">
                  {generatedBundles.map(bundle => {
                    const imgs = getBundleImages(bundle);
                    return (
                      <div
                        key={bundle.id}
                        onClick={() => { setSelectedBundle(bundle); setSelectedAsset(null); }}
                        className={`bg-slate-900 border rounded-2xl p-4 cursor-pointer transition-all ${
                          selectedBundle?.id === bundle.id ? 'border-violet-500' : 'border-slate-700 hover:border-slate-500'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <div className="min-w-0">
                            <p className="text-slate-100 font-semibold truncate">{bundle.trendName}</p>
                            <p className="text-slate-500 text-xs mt-0.5">{fmtDate(bundle.generatedAt)} · {bundle.blogPost?.wordCount?.toLocaleString()} words</p>
                          </div>
                          <span className={`px-2 py-0.5 text-xs rounded-full shrink-0 ${
                            bundle.approvalStatus === 'approved' ? 'bg-green-500/20 text-green-400' :
                            bundle.approvalStatus === 'rejected' ? 'bg-red-500/20 text-red-400' :
                            'bg-amber-500/20 text-amber-400'
                          }`}>{bundle.approvalStatus}</span>
                        </div>
                        <div className="flex flex-wrap gap-2 mb-2">
                          <span className="px-2 py-0.5 bg-blue-500/10 text-blue-300 text-xs rounded-full">📝 Blog</span>
                          {imgs.infographic && <span className="px-2 py-0.5 bg-purple-500/10 text-purple-300 text-xs rounded-full">📊 Infographic</span>}
                          {imgs.comic       && <span className="px-2 py-0.5 bg-amber-500/10  text-amber-300  text-xs rounded-full">🎨 Comic</span>}
                          {bundle.reelScript && <span className="px-2 py-0.5 bg-pink-500/10 text-pink-300 text-xs rounded-full">🎬 Reel</span>}
                        </div>
                        <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                          <button
                            onClick={() => openModal(bundle, 'blog')}
                            className="flex items-center gap-1 px-3 py-1.5 bg-slate-700 hover:bg-violet-700 text-slate-200 text-xs font-medium rounded-lg transition-colors"
                          >
                            <Eye className="w-3.5 h-3.5" /> View Full
                          </button>
                          <button
                            onClick={() => handleBundleDownloadAll(bundle)}
                            className="flex items-center gap-1 px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-200 text-xs font-medium rounded-lg transition-colors"
                          >
                            <Download className="w-3.5 h-3.5" /> Download All
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Right: Detail Panel */}
          <div className="lg:col-span-1">
            <div className={`rounded-xl border p-5 sticky top-6 ${selectedBundle ? 'bg-slate-900 border-slate-700' : 'bg-white border-gray-200'}`}>

              {/* ── Bundle detail panel ── */}
              {selectedBundle ? (() => {
                const imgs = getBundleImages(selectedBundle);
                const blog = selectedBundle.blogPost;
                const rs   = selectedBundle.reelScript as any;
                const body = (blog as any)?.body || blog?.excerpt || '';
                return (
                  <div className="space-y-4">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="text-xs text-violet-400 uppercase tracking-wider mb-1">AI Bundle</p>
                        <h2 className="text-white font-bold text-base leading-snug">{selectedBundle.trendName}</h2>
                        <p className="text-slate-500 text-xs mt-0.5">{fmtDate(selectedBundle.generatedAt)}</p>
                      </div>
                      <span className={`px-2 py-0.5 text-xs rounded-full shrink-0 ${
                        selectedBundle.approvalStatus === 'approved' ? 'bg-green-500/20 text-green-400' :
                        selectedBundle.approvalStatus === 'rejected' ? 'bg-red-500/20 text-red-400' :
                        'bg-amber-500/20 text-amber-400'
                      }`}>{selectedBundle.approvalStatus}</span>
                    </div>

                    {/* Blog Post section */}
                    {blog && (
                      <div className="bg-slate-800 rounded-xl p-4 space-y-2">
                        <div className="flex items-center justify-between">
                          <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold">📝 Blog Post</p>
                          <button
                            onClick={() => openModal(selectedBundle, 'blog')}
                            className="flex items-center gap-1 text-violet-400 hover:text-violet-300 text-xs"
                          >
                            <Eye className="w-3 h-3" /> View Full
                          </button>
                        </div>
                        <p className="text-slate-100 font-medium text-sm">{blog.title}</p>
                        <p className="text-slate-400 text-xs">{blog.wordCount?.toLocaleString()} words · SEO {blog.seoScore}/100</p>
                        <p className="text-slate-400 text-xs line-clamp-3">{blog.excerpt}</p>
                        <div className="flex items-center gap-2 pt-1">
                          <button
                            onClick={() => handlePanelCopy(body)}
                            className="flex items-center gap-1 px-2.5 py-1 bg-slate-700 hover:bg-slate-600 text-slate-300 text-xs rounded-lg transition-colors"
                          >
                            {panelCopied ? <Check className="w-3 h-3 text-green-400" /> : <Copy className="w-3 h-3" />}
                            {panelCopied ? 'Copied!' : 'Copy Text'}
                          </button>
                          <button
                            onClick={() => downloadText(`${selectedBundle.trendName.replace(/\s+/g,'_')}_blog.md`, `# ${blog.title}\n\n${body}`)}
                            className="flex items-center gap-1 px-2.5 py-1 bg-slate-700 hover:bg-slate-600 text-slate-300 text-xs rounded-lg transition-colors"
                          >
                            <Download className="w-3 h-3" /> .md
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Infographic image thumbnail */}
                    {imgs.infographic && (
                      <div className="bg-slate-800 rounded-xl p-4 space-y-2">
                        <div className="flex items-center justify-between">
                          <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold">📊 Infographic</p>
                          <button
                            onClick={() => openModal(selectedBundle, 'infographic')}
                            className="flex items-center gap-1 text-violet-400 hover:text-violet-300 text-xs"
                          >
                            <Eye className="w-3 h-3" /> View Full
                          </button>
                        </div>
                        <img src={imgs.infographic} alt="Infographic" className="w-full rounded-lg object-contain max-h-32 bg-slate-900 border border-slate-700" />
                        <button
                          onClick={() => downloadImage(`${selectedBundle.trendName.replace(/\s+/g,'_')}_infographic.png`, imgs.infographic!)}
                          className="flex items-center gap-1 px-2.5 py-1 bg-slate-700 hover:bg-slate-600 text-slate-300 text-xs rounded-lg transition-colors"
                        >
                          <Download className="w-3 h-3" /> Download PNG
                        </button>
                      </div>
                    )}

                    {/* Comic image thumbnail */}
                    {imgs.comic && (
                      <div className="bg-slate-800 rounded-xl p-4 space-y-2">
                        <div className="flex items-center justify-between">
                          <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold">🎨 Comic Strip</p>
                          <button
                            onClick={() => openModal(selectedBundle, 'comic')}
                            className="flex items-center gap-1 text-violet-400 hover:text-violet-300 text-xs"
                          >
                            <Eye className="w-3 h-3" /> View Full
                          </button>
                        </div>
                        <img src={imgs.comic} alt="Comic Strip" className="w-full rounded-lg object-contain max-h-32 bg-slate-900 border border-slate-700" />
                        <button
                          onClick={() => downloadImage(`${selectedBundle.trendName.replace(/\s+/g,'_')}_comic.png`, imgs.comic!)}
                          className="flex items-center gap-1 px-2.5 py-1 bg-slate-700 hover:bg-slate-600 text-slate-300 text-xs rounded-lg transition-colors"
                        >
                          <Download className="w-3 h-3" /> Download PNG
                        </button>
                      </div>
                    )}

                    {/* Reel Script */}
                    {rs && (
                      <div className="bg-slate-800 rounded-xl p-4 space-y-2">
                        <div className="flex items-center justify-between">
                          <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold">🎬 Reel Script</p>
                          <button
                            onClick={() => openModal(selectedBundle, 'reel')}
                            className="flex items-center gap-1 text-violet-400 hover:text-violet-300 text-xs"
                          >
                            <Eye className="w-3 h-3" /> View Full
                          </button>
                        </div>
                        <p className="text-slate-200 font-medium text-sm">{rs.title || selectedBundle.trendName}</p>
                        {rs.hook && <p className="text-slate-400 text-xs italic line-clamp-2">"{rs.hook}"</p>}
                        <p className="text-slate-500 text-xs">{rs.duration} · {rs.scenes?.length} scenes</p>
                        <button
                          onClick={() => {
                            const text = [`Hook: ${rs.hook || ''}`, '', ...(rs.scenes || []).map((s: any, i: number) => `Scene ${i+1}: ${s.text || s}`), '', `CTA: ${rs.cta || ''}`].join('\n');
                            downloadText(`${selectedBundle.trendName.replace(/\s+/g,'_')}_reel_script.txt`, text);
                          }}
                          className="flex items-center gap-1 px-2.5 py-1 bg-slate-700 hover:bg-slate-600 text-slate-300 text-xs rounded-lg transition-colors"
                        >
                          <Download className="w-3 h-3" /> .txt
                        </button>
                      </div>
                    )}

                    {/* Keywords */}
                    {selectedBundle.trendKeywords && selectedBundle.trendKeywords.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {selectedBundle.trendKeywords.slice(0, 6).map(kw => (
                          <span key={kw} className="px-2 py-0.5 bg-slate-700 text-slate-400 text-xs rounded">#{kw}</span>
                        ))}
                      </div>
                    )}

                    {/* Download all */}
                    <button
                      onClick={() => handleBundleDownloadAll(selectedBundle)}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-violet-600 hover:bg-violet-500 text-white rounded-xl text-sm font-medium transition-colors"
                    >
                      <Download className="w-4 h-4" /> Download All Assets
                    </button>
                  </div>
                );
              })() : selectedAsset ? (
                /* ── Mock asset detail panel ── */
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <div className={`p-2 rounded-lg ${
                        selectedAsset.contentType === 'article'     ? 'bg-blue-100 text-blue-600' :
                        selectedAsset.contentType === 'infographic' ? 'bg-purple-100 text-purple-600' :
                        'bg-pink-100 text-pink-600'
                      }`}>{getAssetIcon(selectedAsset.contentType)}</div>
                      <span className="text-xs font-medium text-gray-500 uppercase">{selectedAsset.contentType}</span>
                    </div>
                    <h2 className="text-xl font-bold text-gray-900 mb-1">{selectedAsset.title}</h2>
                    <p className="text-sm text-gray-600">{selectedAsset.description}</p>
                  </div>

                  <div className="flex items-center justify-between py-3 border-y border-gray-200">
                    {getStatusBadge(selectedAsset.status)}
                    <span className="text-xs text-gray-400">{fmtDate(selectedAsset.createdAt)}</span>
                  </div>

                  {selectedAsset.contentType === 'article' && (
                    <div className="bg-gray-50 rounded-xl p-4 text-sm text-gray-700 leading-relaxed">
                      <p>This article covers the latest trends in <strong>{getTrendName(selectedAsset.trendTopicId)}</strong>, with actionable insights and practical examples.</p>
                      <p className="mt-2 text-gray-500 text-xs">Note: This is a demo asset — full content is available for AI-generated bundles via the 🤖 AI Bundles tab.</p>
                    </div>
                  )}

                  {selectedAsset.contentType === 'infographic' && (
                    <div className="aspect-[3/4] bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 rounded-xl p-6 flex flex-col justify-between">
                      <div className="text-white">
                        <h3 className="text-xl font-bold mb-1">{getTrendName(selectedAsset.trendTopicId)}</h3>
                        <p className="text-sm opacity-80">Visual Data Breakdown</p>
                      </div>
                      <div className="space-y-2">
                        {[['92%','Adoption Rate'],['34%','Growth YoY'],['5.2M','Designers']] .map(([v,l]) => (
                          <div key={l} className="bg-white/20 rounded-lg p-2.5">
                            <div className="text-white text-2xl font-bold">{v}</div>
                            <div className="text-white/70 text-xs">{l}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {selectedAsset.contentType === 'video' && (
                    <div className="aspect-[9/16] bg-gray-900 rounded-xl overflow-hidden relative flex items-center justify-center">
                      <div className="text-center">
                        <div className="w-14 h-14 bg-white/10 rounded-full flex items-center justify-center mb-2 mx-auto">
                          <Video className="w-7 h-7 text-white" />
                        </div>
                        <p className="text-white text-sm font-medium">Video Preview</p>
                        <p className="text-white/50 text-xs mt-1">{selectedAsset.duration}s</p>
                      </div>
                    </div>
                  )}

                  <div>
                    <p className="text-xs font-semibold text-gray-600 mb-2">Tags</p>
                    <div className="flex flex-wrap gap-1.5">
                      {selectedAsset.tags.map((tag, i) => (
                        <span key={i} className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs">#{tag}</span>
                      ))}
                    </div>
                  </div>

                  <div className="flex gap-2 pt-2">
                    <button
                      onClick={() => {
                        const content = `Title: ${selectedAsset.title}\n\nDescription: ${selectedAsset.description}\n\nTrend: ${getTrendName(selectedAsset.trendTopicId)}\nTags: ${selectedAsset.tags.join(', ')}\nStatus: ${selectedAsset.status}`;
                        downloadText(`${selectedAsset.title.replace(/\s+/g,'_')}.txt`, content);
                      }}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-200 transition-colors"
                    >
                      <Download className="w-4 h-4" /> Download
                    </button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <Layers className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 text-sm">Select an asset to view details</p>
                  <p className="text-gray-400 text-xs mt-1">Click any card on the left</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default AssetLibrary;
